import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import loader from "../assets/loader.json";
import { useTransctionhistoryMutation } from "../redux/features/user/userApi";
import Navbar from "../Components/Navbar";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval } from "date-fns";

const Transction = () => {
  const navigate = useNavigate();
  const [transctionhistory, { isSuccess, isError, isLoading, data }] = useTransctionhistoryMutation();
  const [transactions, setTransactions] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  // Date filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // Interval state for graph
  const [interval, setInterval] = useState("day"); // day, week, month, year
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));

  useEffect(() => {
    // Fetch transaction history when component mounts
    transctionhistory();
  }, [transctionhistory]);

  useEffect(() => {
    // Update transactions when API data is received
    if (isSuccess && data?.data) {
      setTransactions(data.data);
    }
  }, [isSuccess, data]);

  // Format date function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle view transaction
  const handleViewTransaction = (transaction) => {
    // Navigate to single transaction page with transaction ID
    navigate(`/transaction/${transaction._id}`);
  };

  // Filter transactions based on search and date range
  const filteredTransactions = transactions
    .filter(txn => {
      const matchesSearch =
    txn.email?.toLowerCase().includes(search.toLowerCase()) ||
    txn.tokenID?.toLowerCase().includes(search.toLowerCase()) ||
    txn.mode?.toLowerCase().includes(search.toLowerCase()) ||
        (txn.date && new Date(txn.date).toLocaleDateString('en-US').includes(search));
      let matchesDate = true;
      if (startDate) {
        matchesDate = matchesDate && new Date(txn.date) >= new Date(startDate);
      }
      if (endDate) {
        // Add 1 day to endDate to make it inclusive
        const end = new Date(endDate);
        end.setDate(end.getDate() + 1);
        matchesDate = matchesDate && new Date(txn.date) < end;
      }
      return matchesSearch && matchesDate;
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  // Graph-scoped month filter for chart
  const graphTransactions = React.useMemo(() => {
    if (!selectedMonth) return filteredTransactions;
    const [yearStr, monthStr] = selectedMonth.split('-');
    const year = Number(yearStr);
    const monthIndex = Number(monthStr) - 1;
    const monthStart = new Date(year, monthIndex, 1);
    const monthEnd = endOfMonth(monthStart);
    return filteredTransactions.filter(txn => {
      if (!txn.date) return false;
      const d = new Date(txn.date);
      return d >= monthStart && d <= monthEnd;
    });
  }, [filteredTransactions, selectedMonth]);

  // --- Transaction Graph Data ---
  const groupedGraphData = React.useMemo(() => {
    const group = {};
    graphTransactions.forEach((txn) => {
      if (!txn.date) return;
      const dateObj = new Date(txn.date);
      let key = "";
      if (interval === "day") {
        key = format(dateObj, "yyyy-MM-dd");
      } else if (interval === "week") {
        const weekNumber = Math.ceil(dateObj.getDate() / 7);
        key = `${dateObj.getFullYear()}-W${String(weekNumber).padStart(2, "0")}`;
      } else if (interval === "month") {
        key = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}`;
      } else if (interval === "year") {
        key = `${dateObj.getFullYear()}`;
      }
      if (!group[key]) group[key] = 0;
      group[key] += 1;
    });

    // Helper: get min/max date from graphTransactions
    const getMinMaxDate = () => {
      if (!graphTransactions.length) return [null, null];
      let min = new Date(graphTransactions[0].date);
      let max = new Date(graphTransactions[0].date);
      graphTransactions.forEach(txn => {
        const d = new Date(txn.date);
        if (d < min) min = d;
        if (d > max) max = d;
      });
      return [min, max];
    };

    // For 'day' interval, fill all days in the selected month
    if (interval === "day") {
      const [yearStr, monthStr] = (selectedMonth || format(new Date(), 'yyyy-MM')).split('-');
      const monthStart = new Date(Number(yearStr), Number(monthStr) - 1, 1);
      const monthEnd = endOfMonth(monthStart);
      const allDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
      return allDays.map(date => {
        const key = format(date, "yyyy-MM-dd");
        return {
          label: format(date, "MMM dd"),
          count: group[key] || 0,
        };
      });
    }
    // For 'week' interval, fill all weeks in the range
    if (interval === "week" && graphTransactions.length > 0) {
      const [min, max] = getMinMaxDate();
      let weeks = [];
      let current = new Date(min);
      current.setDate(1);
      let end = new Date(max);
      end.setDate(1);
      end.setMonth(end.getMonth() + 1);
      while (current <= end) {
        const year = current.getFullYear();
        const month = current.getMonth();
        for (let w = 1; w <= 5; w++) {
          const key = `${year}-W${String(w).padStart(2, "0")}`;
          weeks.push(key);
        }
        current.setMonth(current.getMonth() + 1);
      }
      weeks = [...new Set(weeks)];
      return weeks.map(k => {
        const [year, week] = k.split('-W');
        return {
          label: `W${week} ${year}`,
          count: group[k] || 0,
        };
      });
    }
    // For 'month' interval, fill all months in the range
    if (interval === "month" && graphTransactions.length > 0) {
      const [min, max] = getMinMaxDate();
      let months = [];
      let current = new Date(min.getFullYear(), min.getMonth(), 1);
      const end = new Date(max.getFullYear(), max.getMonth(), 1);
      while (current <= end) {
        const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, "0")}`;
        months.push(key);
        current.setMonth(current.getMonth() + 1);
      }
      return months.map(k => {
        const [year, month] = k.split('-');
        return {
          label: format(new Date(year, Number(month) - 1), "MMM yyyy"),
          count: group[k] || 0,
        };
      });
    }
    // For 'year' interval, fill all years in the range
    if (interval === "year" && graphTransactions.length > 0) {
      const [min, max] = getMinMaxDate();
      let years = [];
      for (let y = min.getFullYear(); y <= max.getFullYear(); y++) {
        years.push(String(y));
      }
      return years.map(k => ({
        label: k,
        count: group[k] || 0,
      }));
    }
    // Sort keys
    const sortedKeys = Object.keys(group).sort();
    return sortedKeys.map((k) => ({
      label: k,
      count: group[k],
    }));
  }, [graphTransactions, interval, selectedMonth]);

  const graphOptions = React.useMemo(() => ({
    chart: {
      type: "areaspline",
      style: {
        fontFamily: "'Inter', 'Segoe UI', Roboto, sans-serif",
      },
      backgroundColor: "#fff",
      borderRadius: 12,
      spacing: [20, 20, 20, 20],
      animation: { duration: 1000 },
      height: 320,
    },
    title: { text: 'Transactions per Day', align: 'left' },
    xAxis: {
      categories: groupedGraphData.map((item) => item.label),
      title: { text: 'Date', style: { fontSize: '12px', fontWeight: '600', color: '#64748B' } },
      gridLineWidth: 1,
      gridLineColor: 'rgba(100, 116, 139, 0.1)',
      lineColor: 'rgba(100, 116, 139, 0.2)',
      tickColor: 'rgba(100, 116, 139, 0.2)',
      labels: { style: { fontSize: '11px', color: '#64748B', fontWeight: '500' } },
      accessibility: { description: 'Dates' },
    },
    yAxis: {
      title: { text: 'Number of Transactions', style: { fontSize: '12px', fontWeight: '600', color: '#64748B' } },
      gridLineColor: 'rgba(100, 116, 139, 0.1)',
      labels: { style: { fontSize: '11px', color: '#64748B', fontWeight: '500' } },
      min: 0,
      allowDecimals: false,
      accessibility: { description: 'Number of transactions' },
    },
    tooltip: {
      backgroundColor: '#FFFFFF',
      borderWidth: 0,
      borderRadius: 8,
      shadow: true,
      style: { fontSize: '13px', color: '#1e293b' },
      headerFormat: `<span style="font-weight: 600; font-size: 13px; color: #1E293B">{point.key}</span><br/>`,
      pointFormat: `<span style="color: #2563eb; font-weight: 500">{point.y}</span> Transactions`,
      animation: true,
      shared: true,
      useHTML: true,
    },
    plotOptions: {
      areaspline: {
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, 'rgba(37, 99, 235, 0.18)'],
            [1, 'rgba(37, 99, 235, 0.03)'],
          ],
        },
        lineWidth: 3,
        color: '#2563eb',
        states: { hover: { lineWidth: 4 } },
        marker: {
          enabled: true,
          radius: 5,
          symbol: 'circle',
          fillColor: '#fff',
          lineWidth: 2,
          lineColor: '#2563eb',
          states: { hover: { enabled: true, radius: 7, fillColor: '#fff' } },
        },
        threshold: null,
      },
    },
    series: [
      {
        name: 'Transactions',
        data: groupedGraphData.map((item) => item.count),
        color: '#2563eb',
        fillOpacity: 0.3,
        accessibility: { description: 'Number of transactions per day' },
      },
    ],
    credits: { enabled: false },
    legend: { enabled: false },
    responsive: {
      rules: [
        {
          condition: { maxWidth: 600 },
          chartOptions: {
            chart: { spacing: [8, 8, 8, 8], height: 220 },
            xAxis: { labels: { rotation: -45 } },
          },
        },
      ],
    },
    accessibility: {
      description: `Chart showing transactions per day`,
      screenReaderSection: {
        beforeChartFormat:
          '<h5>{chartTitle}</h5>' +
          '<div>{chartSubtitle}</div>' +
          '<div>{chartLongdesc}</div>' +
          '<div>{viewTableButton}</div>',
      },
    },
  }), [groupedGraphData]);

  // Calculate paginated transactions
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when search or filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [search, startDate, endDate]);

  // Calculate current page range for display
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, filteredTransactions.length);

  // CSV Export function
  const exportToCSV = () => {
    if (!filteredTransactions.length) return;
    const headers = ["Email", "TokenID", "Amount", "INR Value", "Date"];
    const csvRows = [
      headers.join(","),
      ...filteredTransactions.map(txn =>
        [
          txn.email,
          txn.tokenID,
          txn.amount,
          txn.inr_value,
          formatDate(txn.date)
        ].join(",")
      )
    ];
    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "transactions.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="w-32 h-32">
            <Lottie animationData={loader} loop={true} />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Transactions</h2>
            <p className="text-gray-600">Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Transaction History Graph */}
          <div className="px-6 pt-6">
            <div className="flex items-center gap-2 mb-4">
              {['day', 'week', 'month', 'year'].map((val) => (
                <button
                  key={val}
                  onClick={() => setInterval(val)}
                  className={`px-4 py-1 rounded-lg font-semibold transition-colors duration-150 ${interval === val ? 'bg-blue-600 text-white shadow' : 'bg-transparent text-blue-700 hover:bg-blue-200'}`}
                >
                  {val.charAt(0).toUpperCase() + val.slice(1)}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <span className="text-sm text-gray-600">Month:</span>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="border px-3 py-1 rounded"
                  aria-label="Select month for Transactions graph"
                />
              </div>
            </div>
            <HighchartsReact highcharts={Highcharts} options={graphOptions} />
          </div>
          <div className="px-6 py-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Transaction History</h1>
              {/* <p className="text-gray-600 mt-1">View all your transaction records</p> */}
            </div>
            <div className="flex gap-2 items-center mt-2 md:mt-0">
              <input
                type="text"
                placeholder="Search by email, tokenID, or date..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="border px-3 py-2 rounded"
              />
              {/* Date filters */}
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="border px-3 py-2 rounded"
                title="Start Date"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="border px-3 py-2 rounded"
                title="End Date"
              />
              <button
                onClick={exportToCSV}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Export
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TokenID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Manage
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        <p className="text-lg font-medium">
                          {filteredTransactions.length === 0 && transactions.length > 0 
                            ? "No transactions match your search criteria" 
                            : "No transactions found"}
                        </p>
                        <p className="text-sm">
                          {filteredTransactions.length === 0 && transactions.length > 0 
                            ? "Try adjusting your search or date filters" 
                            : "Your transaction history will appear here"}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedTransactions.map((transaction, index) => (
                    <tr key={transaction._id || index} className="hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {transaction.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {transaction.tokenID}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {transaction.amount} {transaction.currency}
                        </div>
                        {transaction.inr_value > 0 && (
                          <div className="text-xs text-gray-500">
                            ₹{transaction.inr_value}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(transaction.date)}
                        </div>
                      </td>
                      {/* Mode column */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                            ${transaction.mode === 'Transfer' ? 'bg-yellow-100 text-yellow-800' :
                              transaction.mode === 'Sale' ? 'bg-green-100 text-green-800' :
                                transaction.mode === 'Free' ? 'bg-orange-100 text-orange-800' :
                                  'bg-gray-100 text-gray-800'}
                          `}
                        >
                          {transaction.mode || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleViewTransaction(transaction)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                          </svg>
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 py-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 rounded border bg-gray-100 text-gray-700 disabled:opacity-50 hover:bg-gray-200 transition-colors"
                >
                  Previous
                </button>
                
                {/* Page numbers with ellipsis for better UX */}
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  
                  if (totalPages <= maxVisiblePages) {
                    // Show all pages if total is small
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    // Show first page
                    pages.push(1);
                    
                    if (currentPage > 3) {
                      pages.push('...');
                    }
                    
                    // Show pages around current page
                    const start = Math.max(2, currentPage - 1);
                    const end = Math.min(totalPages - 1, currentPage + 1);
                    
                    for (let i = start; i <= end; i++) {
                      if (!pages.includes(i)) {
                        pages.push(i);
                      }
                    }
                    
                    if (currentPage < totalPages - 2) {
                      pages.push('...');
                    }
                    
                    // Show last page
                    if (totalPages > 1) {
                      pages.push(totalPages);
                    }
                  }
                  
                  return pages.map((page, index) => (
                    <button
                      key={index}
                      onClick={() => typeof page === 'number' && setCurrentPage(page)}
                      disabled={typeof page !== 'number'}
                      className={`px-3 py-2 rounded border transition-colors ${
                        page === currentPage
                          ? 'bg-blue-600 text-white border-blue-600'
                          : typeof page === 'number'
                          ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                          : 'bg-transparent text-gray-400 border-transparent cursor-default'
                      }`}
                    >
                      {page}
                    </button>
                  ));
                })()}
                
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 rounded border bg-gray-100 text-gray-700 disabled:opacity-50 hover:bg-gray-200 transition-colors"
                >
                  Next
                </button>
              </div>
              
              {/* Page info */}
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </div>
            </div>
          )}
          {filteredTransactions.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startIndex}</span> to <span className="font-medium">{endIndex}</span> of <span className="font-medium">{filteredTransactions.length}</span> transactions
                  {totalPages > 1 && (
                    <span className="text-gray-500 ml-2">({itemsPerPage} per page)</span>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  Last updated: {new Date().toLocaleString()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Transction;